// GENERATED by helm-console/scripts/ensure-icons.mjs — DO NOT EDIT.
// Source of truth: helm-bridge/src/client/icons-starship.ts
// (itself generated from lucide-static by helm-bridge/scripts/gen-icon-sprite.mjs).
// Regenerated on every console build; edits here are overwritten.
// The console and the release UI share ONE icon set on purpose — see the
// header of ensure-icons.mjs.
// icons-starship.ts — STARSHIP's ICON SET.
//
// THE SHARED ID CONTRACT. A style does not get its own id space: it answers
// the SAME symbol ids the Blueprint sprite defines, so every call site in the
// app is unchanged and a style switch is a sprite swap, not a refactor. Two
// rules make that safe and scripts/check-icon-refs.mjs enforces both with
// measured counts:
//
//   1. NO ORPHANS. Every id below exists in the Blueprint sprite. An id only
//      this set defined would render for STARSHIP and blank for everyone
//      else — the silent-blank failure the icons gate exists to refuse.
//   2. TOTAL COVERAGE (owner ruling 2026-09-07). This set answers EVERY id
//      the Blueprint sprite defines — 127 of 127 — in Starship's own idiom.
//      The composition fallback in icons.ts stays as the mechanism that can
//      never leave a slot empty, but for a styled set it is now a BUILD-TIME
//      DIAGNOSTIC only: check-icon-refs.mjs is red, by id name, the moment
//      this set drops one. The owner saw the previous rule in production —
//      a Helm nav mixing this style's glyphs with smooth Blueprint ones —
//      and ruled that a mixed set is not a shipped state.
//
// THE DRAWING RULES (spec section 6, ruling R21). ONE stroke weight
// throughout — 1.5, set once in styles.css so the whole set follows one
// number — MITER joins, BUTT caps, a 20-unit grid inset in the shared 24
// viewBox (every coordinate lives in 2..22), NO ROUNDING and NO 45-degree
// chamfers (the chamfer is Destroyer's, R11). No fills anywhere. Glass's set
// is 1.25 round-on-24 and Quarterdeck's is bevel+butt-on-24; the three are
// separated by construction, not by subject.
//
// HOW THE 127 ARE BUILT. The hexagonal VIEWPORT is Starship's mark (R9/R22)
// and it is the shape the set is built from: the brand glyphs are the
// viewport itself, and the whole status ladder — plus every `circle-*` id,
// which is the same claim in Blueprint's vocabulary — is the viewport with
// one state mark inside it. That is what makes a row of chips scannable by
// silhouette before colour. Around it sit the instruments the thesis is
// about (a needle gauge, an attitude indicator, a ranging fan, a sighting
// scope), and around THOSE the console vocabulary the rest of the app needs:
// PANELS (square frames, sometimes double-framed as a HUD reticle), BARS AND
// GRADUATIONS, square-cut CHEVRONS and ARROWHEADS, and an OCTAGONAL RING for
// the rotate/refresh/history family — the ring is deliberately not the
// viewport hexagon, so "this is a cycle" never reads as "this is a status".
//
// SECONDARY INK. A stroke that carries context rather than the subject —
// beams, graduations, ghosted partitions — is drawn at opacity .35–.75. That
// is the only intensity axis in the set; there is no second stroke weight.
//
// NO DEGENERATE DOTS. Lucide draws a dot as `d="M12 17h.01"`, which relies on
// a ROUND cap. This set is butt-capped (see the rules above), so a zero-length
// path here paints NOTHING — measured on `circle-alert`, which shipped one.
// Every dot in this set is a small square instead.

/** The viewport: a regular pointy-top hexagon, r = 9 on the 20 grid. Six
 * miter joins, no curve anywhere. Starship's alone across the programme. */
const HEX = '<path d="M12 3 19.79 7.5 19.79 16.5 12 21 4.21 16.5 4.21 7.5Z" />';
/** The inner viewport, r = 6.2 — the second ring of the status ladder. */
const HEX_INNER = '<path d="M12 5.8 17.37 8.9 17.37 15.1 12 18.2 6.63 15.1 6.63 8.9Z" opacity=".5" />';

/** THE MARK — the viewport with a single vertical needle through its centre.
 * It reads as a ship's window, as an attitude indicator, and — rotated 90 and
 * dimmed — as a capital H for Helm, the hexagon's two vertical sides becoming
 * the uprights. No other mark in the programme claims the hexagon or the
 * hidden H (R9). */
const VIEWPORT = HEX + '<path d="M12 3.4V20.6" />';

/** The CYCLE ring — an axis-aligned octagon on the same 20 grid. Deliberately
 * NOT the viewport hexagon: rotate / refresh / history / tack are motion, not
 * status, and the two families must never be confused at rail size. Drawn
 * open (a gap where the arrowhead enters) by each consumer, so this constant
 * is only the closed form used by `badge-check`. */
const OCT = '<path d="M8.3 3.4 15.7 3.4 20.6 8.3 20.6 15.7 15.7 20.6 8.3 20.6 3.4 15.7 3.4 8.3Z" />';

/** The PANEL — the square frame most console glyphs are built in, inset to
 * the 20 grid so it lines up with the viewport's extremes. */
const PANEL = '<path d="M3.4 3.4h17.2v17.2H3.4z" />';

/** A CLIPBOARD body + its clip: three ids share it, so it is written once. */
const CLIP =
  '<path d="M5 4.6h14v16H5z" /> <path d="M9 2.6h6v3.4H9z" />';

/**
 * id -> symbol INNER markup (the `<symbol>` wrapper and the viewBox are added
 * by icons.ts, so a set cannot get either wrong).
 */
export const STARSHIP_SYMBOLS: Record<string, string> = {
  // ---- the viewport family -------------------------------------------
  helm: VIEWPORT,
  'ship-wheel': VIEWPORT,
  porthole: HEX + HEX_INNER,

  // ---- the instruments -------------------------------------------------
  compass:
    '<circle cx="12" cy="12" r="9" /> <path d="M12 3v2.6" /> <path d="M21 12h-2.6" />' +
    ' <path d="M12 21v-2.6" /> <path d="M3 12h2.6" /> <path d="M8.4 15.6 15.6 8.4" />' +
    ' <path d="M10.6 10.6h2.8v2.8h-2.8z" />',
  gauge:
    '<path d="M3.4 18.6A10 10 0 0 1 20.6 18.6" /> <path d="M12 16.6 16.8 9.4" />' +
    ' <path d="M10.8 16.6h2.4v2.4h-2.4z" /> <path d="M5.4 12.1 7.1 12.8" opacity=".6" />' +
    ' <path d="M12 5.1v1.8" opacity=".6" /> <path d="M18.6 12.1 16.9 12.8" opacity=".6" />',
  horizon:
    '<path d="M2.6 12h18.8" /> <path d="M7.6 8.2 12 12 16.4 8.2" /> <path d="M12 3v3.2" />' +
    ' <path d="M4.4 15.8h4.2" /> <path d="M15.4 15.8h4.2" /> <path d="M12 17.8V21" />',
  sonar:
    '<path d="M5.4 4.6v14.8" /> <path d="M9 8.4A5 5 0 0 1 9 15.6" />' +
    ' <path d="M12.6 6A8.4 8.4 0 0 1 12.6 18" opacity=".75" />' +
    ' <path d="M16.2 3.4A11.8 11.8 0 0 1 16.2 20.6" opacity=".5" />',
  telescope:
    '<path d="M4.4 15.4 15.4 4.4 19.6 8.6 8.6 19.6Z" /> <path d="M12.6 15.6 14.8 21" />' +
    ' <path d="M8.6 19.6 6.4 21" /> <path d="M16.6 6.4 18 3" opacity=".6" />',
  buoy:
    '<path d="M12 3 15.2 9H8.8Z" /> <path d="M12 9v6" /> <path d="M4.6 15h14.8v3.6H4.6z" />' +
    ' <path d="M4.6 21h14.8" opacity=".5" />',
  eye:
    '<path d="M2.6 12 7 7.4h10L21.4 12 17 16.6H7Z" /> <path d="M10.4 10.4h3.2v3.2h-3.2z" />',
  search:
    '<path d="M4 4h11v11H4z" /> <path d="M15 15 20.6 20.6" />' +
    ' <path d="M6.6 12V6.6h5.4" opacity=".55" />',

  // ---- the mode pair ----------------------------------------------------
  sun:
    '<circle cx="12" cy="12" r="4.4" /> <path d="M12 2.6v2.4" /> <path d="M12 19v2.4" />' +
    ' <path d="M2.6 12H5" /> <path d="M19 12h2.4" /> <path d="m5.3 5.3 1.7 1.7" />' +
    ' <path d="m17 17 1.7 1.7" /> <path d="m18.7 5.3-1.7 1.7" /> <path d="m7 17-1.7 1.7" />',
  moon: '<path d="M20.2 15.4A9 9 0 1 1 8.6 3.8 7.2 7.2 0 0 0 20.2 15.4Z" />',

  // ---- the status ladder: ONE viewport, seven states ---------------------
  'st-ring': HEX + HEX_INNER,
  'st-half': HEX + '<path d="M6.6 12h10.8" /> <path d="M12 12v6.6" opacity=".6" />',
  'st-check': HEX + '<path d="m8.3 12.1 2.7 2.7 5-5.2" />',
  'st-cross': HEX + '<path d="m15.2 8.8-6.4 6.4" /> <path d="m8.8 8.8 6.4 6.4" />',
  'st-pause': HEX + '<path d="M10.2 9v6" /> <path d="M13.8 9v6" />',
  // the DASHED viewport — one short segment per edge, gapped at every vertex
  'st-dash':
    '<path d="M14 4.15 17.9 6.4" /> <path d="M19.79 9.5v5" /> <path d="M17.9 17.6 14 19.85" />' +
    ' <path d="M10 19.85 6.1 17.6" /> <path d="M4.21 14.5v-5" /> <path d="M6.1 6.4 10 4.15" />',
  'st-seal': HEX + HEX_INNER + '<path d="m9.4 12.1 2.2 2.2 3.6-3.8" />',

  // ---- the circle-* family: the SAME viewport, one mark inside -----------
  // Blueprint draws these on a circle; Starship draws them on the viewport,
  // so an alert chip and a status chip share one silhouette and differ only
  // by their mark. `circle-alert`'s dot was `h.01` and painted nothing under
  // this set's butt caps — it is a square now.
  'circle-alert': HEX + '<path d="M12 7.4v5.2" /> <path d="M11.3 15.5h1.4v1.4h-1.4z" />',
  'circle-check': HEX + '<path d="m8.3 12.1 2.7 2.7 5-5.2" />',
  'circle-x': HEX + '<path d="m15.2 8.8-6.4 6.4" /> <path d="m8.8 8.8 6.4 6.4" />',
  'circle-dot': HEX + '<path d="M10.4 10.4h3.2v3.2h-3.2z" />',
  'circle-arrow-up': HEX + '<path d="M12 16.4V8.2" /> <path d="m8.4 11.8 3.6-3.6 3.6 3.6" />',
  'circle-help':
    HEX +
    '<path d="M9.8 10.2V8.8h4.4v3.2L12 14v1.2" /> <path d="M11.3 16.6h1.4v1.4h-1.4z" />',
  ban: HEX + '<path d="m7.6 16.4 8.8-8.8" />',
  clock: HEX + '<path d="M12 7.4V12h3.8" />',

  // ---- the console: panels, terminals, grids -----------------------------
  square: PANEL + '<path d="M6.6 6.6h10.8v10.8H6.6z" opacity=".45" />',
  'square-terminal':
    PANEL + '<path d="m7.4 8.6 3 3-3 3" /> <path d="M12.6 14.6h4.4" />',
  terminal: '<path d="m4.4 6 6 6-6 6" /> <path d="M12.6 18h7" />',
  'grid-2x2': PANEL + '<path d="M12 3.4v17.2" /> <path d="M3.4 12h17.2" />',
  'panels-top-left': PANEL + '<path d="M3.4 9h17.2" /> <path d="M9.4 9v11.6" />',
  workflow:
    '<path d="M3.4 3.4h7v7h-7z" /> <path d="M13.6 13.6h7v7h-7z" />' +
    ' <path d="M6.9 10.4v6.7h6.7" />',
  activity: '<path d="M2.6 12h4l2.4-7 4 14 2.4-7h5.4" />',
  server:
    '<path d="M3.4 3.6h17.2v6.6H3.4z" /> <path d="M3.4 13.8h17.2v6.6H3.4z" />' +
    ' <path d="M6.4 6h1.8v1.8H6.4z" /> <path d="M6.4 16.2h1.8V18H6.4z" />' +
    ' <path d="M15 6.9h3.4" opacity=".6" /> <path d="M15 17.1h3.4" opacity=".6" />',
  database:
    '<path d="M4.4 6.6 12 4 19.6 6.6v10.8L12 20l-7.6-2.6z" />' +
    ' <path d="m4.4 10.2 7.6 2.6 7.6-2.6" opacity=".75" />' +
    ' <path d="m4.4 13.8 7.6 2.6 7.6-2.6" opacity=".75" />',
  house:
    '<path d="M3.4 10.6 12 3.4l8.6 7.2" /> <path d="M5.4 12.2v8.4h13.2v-8.4" />' +
    ' <path d="M10 20.6v-5.2h4v5.2" />',

  // ---- the ship's own vocabulary ----------------------------------------
  anchor:
    '<path d="M11 3.2h2v2h-2z" /> <path d="M12 5.2v15.4" /> <path d="M8.8 8.4h6.4" />' +
    ' <path d="M4.8 12.8v2.4l7.2 5.4 7.2-5.4v-2.4" />',
  ship:
    '<path d="M4.4 13.4h15.2l-2.4 6.4H6.8z" /> <path d="M6.8 8.2h10.4v5.2H6.8z" />' +
    ' <path d="M12 4v4.2" /> <path d="M3.2 21.2h17.6" opacity=".55" />',
  cargo:
    '<path d="M4 6.6h16v11.4H4z" /> <path d="M4 10.6h16" />' +
    ' <path d="m4.4 11 15.2 6.6" opacity=".55" /> <path d="m19.6 11-15.2 6.6" opacity=".55" />',
  box:
    '<path d="M4 6.4h16v13.2H4z" /> <path d="M4 10.4h16" />' +
    ' <path d="M12 10.4v9.2" opacity=".6" /> <path d="M9.6 6.4v4" opacity=".5" />' +
    ' <path d="M14.4 6.4v4" opacity=".5" />',
  container:
    '<path d="M3.4 7.4h17.2v9.2H3.4z" /> <path d="M6.6 7.4v9.2" opacity=".6" />' +
    ' <path d="M9.8 7.4v9.2" opacity=".6" /> <path d="M13 7.4v9.2" opacity=".6" />' +
    ' <path d="M16.2 7.4v9.2" opacity=".6" /> <path d="M3.4 5.2h17.2" opacity=".5" />' +
    ' <path d="M3.4 18.8h17.2" opacity=".5" />',
  // A tower crane: mast, jib, counterweight, hoist, hook. Its first cut was an
  // L of two strokes and read as a bare bracket.
  crane:
    '<path d="M6.6 20.6V4.6" /> <path d="M3.4 20.6h6.4" /> <path d="M3.4 4.6h16.6" />' +
    ' <path d="m6.6 8.6 4.4-4" opacity=".55" /> <path d="M17.4 4.6V10" />' +
    ' <path d="M15.8 10h3.2v3.2h-3.2z" /> <path d="M3.4 2.6h2.2v2h-2.2z" opacity=".7" />',
  knot:
    '<path d="M3.4 9.6h17.2" /> <path d="M3.4 14.4h17.2" /> <path d="M9.6 3.4v17.2" />' +
    ' <path d="M14.4 3.4v17.2" /> <path d="M9.6 9.6h4.8v4.8H9.6z" opacity=".6" />',
  flag:
    '<path d="M5.6 3.2v17.6" /> <path d="M5.6 4.4h13.6l-2.8 3.8 2.8 3.8H5.6" />',
  wave:
    '<path d="M2.6 15.4h3.6V8.6h4.8v6.8h4.8V8.6h3.6" />' +
    ' <path d="M2.6 19.4h18.8" opacity=".5" /> <path d="M2.6 4.6h18.8" opacity=".35" />',
  dividers:
    '<path d="M11.2 4.4h1.6v2.6h-1.6z" /> <path d="M12 7 6.6 20.4" />' +
    ' <path d="m12 7 5.4 13.4" /> <path d="M9.4 13.6h5.2" opacity=".6" />',
  lighthouse:
    '<path d="M9.4 9.6 14.6 9.6 15.8 20.6 8.2 20.6Z" /> <path d="M9.8 6.2h4.4v3.4H9.8z" />' +
    ' <path d="M12 3.4v2.8" /> <path d="M8.8 15.4h6.4" opacity=".6" />' +
    ' <path d="M9.4 7.9 4.6 6.4" opacity=".55" /> <path d="M14.6 7.9 19.4 6.4" opacity=".55" />',

  // ---- comms, sensors, signals ------------------------------------------
  antenna:
    '<path d="M12 21V9.4" /> <path d="M9 21h6" /> <path d="M8.4 9.4h7.2L12 4.6z" />' +
    ' <path d="m5.4 10.6-2.6-2.6 2.6-2.6" opacity=".6" />' +
    ' <path d="m18.6 10.6 2.6-2.6-2.6-2.6" opacity=".6" />',
  signal:
    '<path d="M3.4 17.4h3v3.2h-3z" /> <path d="M8 13.4h3v7.2H8z" />' +
    ' <path d="M12.6 9.4h3v11.2h-3z" /> <path d="M17.2 5.4h3v15.2h-3z" opacity=".55" />',
  chart:
    '<path d="M3.6 3.4v17h17" /> <path d="m6.6 15.6 3.4-3.4 3 3 4.6-6.2" />' +
    ' <path d="M16.6 7.8h2.4v2.4h-2.4z" opacity=".6" />',
  zap: '<path d="M13.4 2.8 5 13.4h5.2L9.6 21.2 18.6 10.4H13z" />',
  camera:
    '<path d="M2.6 7.4h18.8v12.6H2.6z" /> <path d="M8.2 7.4 9.8 4.6h4.4l1.6 2.8" />' +
    ' <path d="M9 10.6h6v6H9z" /> <path d="M17.4 9.4h2v1.6h-2z" opacity=".6" />',
  microscope:
    '<path d="M9 4.2 12.6 3 15 9.6 11.4 10.9Z" /> <path d="m11.4 10.9 1 2.7" />' +
    ' <path d="M7.6 13.6h7.6v2.8H7.6z" /> <path d="M3.6 20.6h16.8" />' +
    ' <path d="M15.2 16.4h3.6v4.2" opacity=".7" />',
  stethoscope:
    '<path d="M5 3.2v6l2.6 2.6h3.4l2.6-2.6v-6" /> <path d="M3.6 3.2h2.8" />' +
    ' <path d="M12.2 3.2h2.8" /> <path d="M9.3 11.8v5.4h3.5" />' +
    ' <path d="M12.8 15.2h4v4h-4z" />',
  'flask-conical':
    '<path d="M9.6 3.4 9.6 9.2 4.6 20.6 19.4 20.6 14.4 9.2 14.4 3.4Z" />' +
    ' <path d="M7.4 15.2h9.2" opacity=".7" /> <path d="M8.6 3.4h6.8" />',
  scale:
    '<path d="M12 4.6v16" /> <path d="M4.2 7h15.6" /> <path d="M8.2 20.6h7.6" />' +
    ' <path d="M4.2 7 2.4 13.6h3.6z" /> <path d="M19.8 7 18 13.6h3.6z" />' +
    ' <path d="M11 3h2v1.6h-2z" opacity=".6" />',

  // ---- records, registers, documents ------------------------------------
  // A CLOSED book: cover, spine block, hinge, and a marker tab. Its first cut
  // was cover + two rules, which rendered as `scroll-text` at 24px.
  book:
    '<path d="M6.6 3.4h13.4v17.2H6.6z" /> <path d="M4 4.6h2.6v14.8H4z" />' +
    ' <path d="M8.6 3.4v17.2" opacity=".5" /> <path d="M13.4 3.4v4.6l1.8-1.4 1.8 1.4V3.4" />',
  'book-open':
    '<path d="M12 6.6v13.2" /> <path d="M12 6.6 3.6 4.6 3.6 17.8 12 19.8" />' +
    ' <path d="M12 6.6 20.4 4.6 20.4 17.8 12 19.8" />' +
    ' <path d="M5.8 8.8h3.8" opacity=".55" /> <path d="M14.4 8.8h3.8" opacity=".55" />',
  library:
    '<path d="M4 6.4h3v13.2H4z" /> <path d="M7.6 4.4h3v15.2h-3z" />' +
    ' <path d="M11.2 7.6h3v12h-3z" /> <path d="M14.8 5.6h3v14h-3z" />' +
    ' <path d="M2.6 19.6h18.8" />',
  logbook:
    '<path d="M6.6 3h13.4v18H6.6z" /> <path d="M3.4 6h3.2" /> <path d="M3.4 10h3.2" />' +
    ' <path d="M3.4 14h3.2" /> <path d="M3.4 18h3.2" />' +
    ' <path d="M9.6 8.4h7.8" opacity=".7" /> <path d="M9.6 12h7.8" opacity=".7" />' +
    ' <path d="M9.6 15.6h5.2" opacity=".7" />',
  'notebook-tabs':
    '<path d="M6.6 3h13.8v18H6.6z" /> <path d="M3.6 6.2h3" /> <path d="M3.6 10.4h3" />' +
    ' <path d="M3.6 14.6h3" /> <path d="M3.6 18.8h3" /> <path d="M15 3v18" />' +
    ' <path d="M15 8h5.4" opacity=".6" /> <path d="M15 12h5.4" opacity=".6" />' +
    ' <path d="M15 16h5.4" opacity=".6" />',
  // The rollers stand OFF the sheet with a spindle between them; drawn flush
  // (the first cut) the three rectangles read as one concentric frame.
  scroll:
    '<path d="M7.4 4.6h9.2v14.8H7.4z" /> <path d="M2.8 6.6h3v10.8h-3z" />' +
    ' <path d="M18.2 6.6h3v10.8h-3z" /> <path d="M5.8 12h1.6" /> <path d="M16.6 12h1.6" />',
  'scroll-text':
    '<path d="M7.4 4.6h9.2v14.8H7.4z" /> <path d="M2.8 6.6h3v10.8h-3z" />' +
    ' <path d="M18.2 6.6h3v10.8h-3z" /> <path d="M5.8 12h1.6" /> <path d="M16.6 12h1.6" />' +
    ' <path d="M9.4 9h5.2" opacity=".8" /> <path d="M9.4 12h5.2" opacity=".8" />' +
    ' <path d="M9.4 15h3.4" opacity=".8" />',
  map:
    '<path d="M3.4 5.6 9 3.6v14.8l-5.6 2z" /> <path d="M9 3.6 15 5.6v14.8L9 18.4z" />' +
    ' <path d="M15 5.6 20.6 3.6v14.8l-5.6 2z" />',
  'clipboard-check': CLIP + ' <path d="m8.6 13.4 2.6 2.6 5-5.2" />',
  'clipboard-list':
    CLIP +
    ' <path d="M11.4 11h4.8" /> <path d="M11.4 15.4h4.8" />' +
    ' <path d="M7.8 10.2h1.6v1.6H7.8z" /> <path d="M7.8 14.6h1.6v1.6H7.8z" />',
  gate:
    '<path d="M4 3.6h16v16.8H4z" /> <path d="M4 7.4h16" opacity=".6" />' +
    ' <path d="M9.6 3.6v3.8" opacity=".5" /> <path d="M14.4 3.6v3.8" opacity=".5" />' +
    ' <path d="m8 13.6 2.6 2.6 5.4-5.6" />',
  'badge-check': OCT + ' <path d="m8.4 12.2 2.6 2.6 5-5.2" />',
  shield:
    '<path d="M12 3.2 20 5.8 20 12.4 12 20.8 4 12.4 4 5.8Z" />' +
    ' <path d="M12 6.6 16.8 8.2 16.8 12.2 12 17.2 7.2 12.2 7.2 8.2Z" opacity=".5" />',
  'folder-open':
    '<path d="M3.4 19.6V4.6h5.6l2 2.6h7.4v3.4" /> <path d="M3.4 19.6 6.6 11h15l-3.2 8.6z" />',
  'file-pen':
    '<path d="M5 3.4h8.6l5.4 5.4v11.8H5z" /> <path d="M13.6 3.4v5.4H19" />' +
    ' <path d="m8.6 17.6 5.6-5.6 2 2-5.6 5.6H8.6z" />',
  bookmark: '<path d="M5.4 3.4h13.2v17.6L12 17l-6.6 4z" />',
  star:
    '<path d="M12 3 14.4 9.4 21 9.8 15.9 14 17.6 20.4 12 16.8 6.4 20.4 8.1 14 3 9.8 9.6 9.4Z" />',

  // ---- people, mail, assistance ------------------------------------------
  crew:
    '<path d="M6.6 4.8h4.6v4.6H6.6z" /> <path d="M2.8 20.4v-3.8l2.2-2.8h8.2l2.2 2.8v3.8" />' +
    ' <path d="M14.8 5.8h3.8v3.8h-3.8z" opacity=".6" />' +
    ' <path d="M16.4 13.8h2.4l2.4 2.8v3.8" opacity=".6" />',
  'user-plus':
    '<path d="M6.6 4.6h4.8v4.8H6.6z" /> <path d="M2.8 20.4v-4l2.2-2.8h8.2l2.2 2.8v4" />' +
    ' <path d="M18.4 6.4v6" /> <path d="M15.4 9.4h6" />',
  mail: '<path d="M2.6 5.4h18.8v13.2H2.6z" /> <path d="m2.6 6.6 9.4 6.6 9.4-6.6" />',
  'message-square':
    '<path d="M3.4 3.4h17.2v13.2H8.6l-4 4v-4H3.4z" />',
  assistant:
    '<path d="M12 2.6 14.1 12 12 21.4 9.9 12Z" /> <path d="M2.6 12 12 9.9 21.4 12 12 14.1Z" />' +
    ' <path d="M17.6 4.4h2.8" opacity=".6" /> <path d="M19 3v2.8" opacity=".6" />',

  // ---- work: build, fix, deploy -------------------------------------------
  gear:
    '<path d="M9 7.2 15 7.2 18 10.2 18 13.8 15 16.8 9 16.8 6 13.8 6 10.2Z" />' +
    ' <path d="M10.4 10.4h3.2v3.2h-3.2z" /> <path d="M9.4 7.2V4.4h5.2v2.8" />' +
    ' <path d="M9.4 16.8v2.8h5.2v-2.8" /> <path d="M6 10.2H3.4v3.6H6" />' +
    ' <path d="M18 10.2h2.6v3.6H18" />',
  // An OPEN-END spanner. The first cut was a closed square head on a diagonal
  // handle, which at 24px is `search` mirrored; the jaw notch is what makes
  // the two unmistakable.
  wrench:
    '<path d="M13.2 3.6h7v3h-3v2h3v2.6h-7z" />' +
    ' <path d="M13.2 11.2 4.4 20v1.4h1.4l8.8-8.8z" />',
  hammer:
    '<path d="M11.6 3.6 20.2 3.6 20.2 8.2 11.6 8.2 10 5.9Z" />' +
    ' <path d="M14 8.2 5.4 16.8" /> <path d="M3.6 18.6 5.4 16.8l2 2-1.8 1.8z" />',
  bug:
    '<path d="M8.2 8.6h7.6v6.8L12 19.8 8.2 15.4z" /> <path d="M12 8.6v11.2" opacity=".6" />' +
    ' <path d="M9.4 5.4h5.2v3.2H9.4z" /> <path d="M4.2 10.4h4" /> <path d="M15.8 10.4h4" />' +
    ' <path d="M4.6 15.4h3.2" opacity=".7" /> <path d="M16.2 15.4h3.2" opacity=".7" />' +
    ' <path d="m9 5.4-2-2.2" opacity=".6" /> <path d="m15 5.4 2-2.2" opacity=".6" />',
  siren:
    '<path d="M7.4 17.8v-5.4l2.4-3.4h4.4l2.4 3.4v5.4" />' +
    ' <path d="M4.6 17.8h14.8v3H4.6z" /> <path d="M10.8 12.4h2.4v5.4h-2.4z" opacity=".5" />' +
    ' <path d="M12 3v3.2" opacity=".6" /> <path d="m4.6 6.4 2.2 2.2" opacity=".6" />' +
    ' <path d="m19.4 6.4-2.2 2.2" opacity=".6" />',
  bell:
    '<path d="M7 16.4v-6l2.6-3.4h4.8l2.6 3.4v6" /> <path d="M4.4 16.4h15.2v2.6H4.4z" />' +
    ' <path d="M10.2 21h3.6" /> <path d="M11.2 3.4h1.6v3.6h-1.6z" />',
  layers:
    '<path d="M12 3.4 21 8 12 12.6 3 8Z" /> <path d="m3 12 9 4.6 9-4.6" opacity=".7" />' +
    ' <path d="m3 16 9 4.6 9-4.6" opacity=".45" />',
  puzzle:
    '<path d="M4 4h7.2v3.2h3.2V4H20v7.2h-3.2v3.2H20V20h-7.2v-3.2H9.6V20H4z" />',
  hourglass:
    '<path d="M5.4 3.4h13.2" /> <path d="M5.4 20.6h13.2" />' +
    ' <path d="M7 3.4v3.8L12 12l5-4.8V3.4" /> <path d="M7 20.6v-3.8L12 12l5 4.8v3.8" />',
  'trash-2':
    '<path d="M5.4 6.4h13.2v14.2H5.4z" /> <path d="M3.4 6.4h17.2" />' +
    ' <path d="M9 3.4h6v3H9z" /> <path d="M10 10.2v6.6" opacity=".7" />' +
    ' <path d="M14 10.2v6.6" opacity=".7" />',
  pencil:
    '<path d="M3.4 20.6v-4l12-12 4 4-12 12z" /> <path d="m13.4 6.4 4 4" opacity=".6" />',
  'square-pen':
    '<path d="M12 3.4H3.4v17.2h17.2V12" /> <path d="m8.6 15.4 8.2-8.2 2.8 2.8-8.2 8.2H8.6z" />',
  filter: '<path d="M3.4 4.4h17.2L14 12.6v6.6l-4 2v-8.6z" />',

  // ---- version control ----------------------------------------------------
  'git-branch':
    '<path d="M5.4 3.4h4v4h-4z" /> <path d="M7.4 7.4v13.2" />' +
    ' <path d="M14.6 3.4h4v4h-4z" /> <path d="M16.6 7.4V12H7.4" />',
  'git-merge':
    '<path d="M4.2 4.2h4v4h-4z" /> <path d="M6.2 8.2v11.6" />' +
    ' <path d="M15.8 15.8h4v4h-4z" /> <path d="M6.2 12h9.6v3.8" />',
  'git-pull-request':
    '<path d="M4.4 4.4h4v4h-4z" /> <path d="M6.4 8.4v11.4" />' +
    ' <path d="M4.4 19.8h4" opacity=".6" /> <path d="M15.8 15.8h4v4h-4z" />' +
    ' <path d="M8.4 6.4h9.4v9.4" />',

  // ---- motion: the OCTAGONAL cycle ring ----------------------------------
  'rotate-cw':
    '<path d="M13.6 3.4H8.3L3.4 8.3v7.4l4.9 4.9h7.4l4.9-4.9V8.3l-3.6-3.6" />' +
    ' <path d="M13.4 2.4 16.8 5.2 13.4 8" />',
  tack:
    '<path d="M13.6 3.4H8.3L3.4 8.3v7.4l4.9 4.9h7.4l4.9-4.9V8.3l-3.6-3.6" />' +
    ' <path d="M13.4 2.4 16.8 5.2 13.4 8" /> <path d="M10.4 10.4h3.2v3.2h-3.2z" opacity=".55" />',
  'refresh-cw':
    '<path d="M3.4 13.6V8.3l4.9-4.9h7.4l2.6 2.6" /> <path d="M18.2 3.6v3.4h-3.4" />' +
    ' <path d="M20.6 10.4v5.3l-4.9 4.9H8.3l-2.6-2.6" /> <path d="M5.8 20.4V17h3.4" />',
  history:
    '<path d="M10.4 3.4H8.3L3.4 8.3v7.4l4.9 4.9h7.4l4.9-4.9V8.3l-2.6-2.6" />' +
    ' <path d="M10.6 2.4 7.2 5.2l3.4 2.8" /> <path d="M12 8v4.4h3.6" />',
  'list-restart':
    '<path d="M3.4 5h11" /> <path d="M3.4 9.6h6.4" /> <path d="M3.4 14.2h6.4" />' +
    ' <path d="M12.4 13.2h5.4l2.8 2.8v2.4l-2.4 2.4h-5.8" />' +
    ' <path d="M14.4 10.8 11.8 13.2l2.6 2.4" />',
  play: '<path d="M7.6 3.8 19.6 12 7.6 20.2Z" />',

  // ---- arrows, chevrons, transfer -----------------------------------------
  chev: '<path d="m9.4 4.6 7.4 7.4-7.4 7.4" />',
  'chevron-down': '<path d="m4.6 8.4 7.4 7.4 7.4-7.4" />',
  'chevron-up': '<path d="m19.4 15.6-7.4-7.4-7.4 7.4" />',
  'chevron-left': '<path d="m14.6 4.6-7.4 7.4 7.4 7.4" />',
  'chevrons-right': '<path d="m5 6.4 5.6 5.6L5 17.6" /> <path d="m12.4 6.4 5.6 5.6-5.6 5.6" />',
  'arrow-left': '<path d="M4 12h16" /> <path d="m11 5-7 7 7 7" />',
  'arrow-right': '<path d="M4 12h16" /> <path d="m13 5 7 7-7 7" />',
  'arrow-up-right': '<path d="M7.4 7.4h9.2v9.2" /> <path d="M7.4 16.6 16.6 7.4" />',
  plus: '<path d="M4.6 12h14.8" /> <path d="M12 4.6v14.8" />',
  x: '<path d="m5.6 5.6 12.8 12.8" /> <path d="M18.4 5.6 5.6 18.4" />',
  check: '<path d="m4 12.4 5.4 5.4L20 6.6" />',
  send: '<path d="M3 12 21 3.4 12.4 21.4 10.4 13.6Z" /> <path d="m10.4 13.6 10.6-10.2" opacity=".6" />',
  reply: '<path d="m9 17.4-5.4-5.4L9 6.6" /> <path d="M3.6 12h13v6.4" />',
  'undo-2': '<path d="m9 15.4-5.4-5.4L9 4.6" /> <path d="M3.6 10h11.6v10.6h-4.8" />',
  download:
    '<path d="M12 3.4v11.2" /> <path d="m7 9.6 5 5 5-5" /> <path d="M3.6 15.6v5h16.8v-5" />',
  upload:
    '<path d="M12 14.6V3.4" /> <path d="m7 8.4 5-5 5 5" /> <path d="M3.6 15.6v5h16.8v-5" />',
  'log-in': '<path d="m9 7 5 5-5 5" /> <path d="M14 12H3.4" /> <path d="M16 3.4h4.6v17.2H16" />',
  'log-out':
    '<path d="m15.6 7 5 5-5 5" /> <path d="M20.6 12H10" /> <path d="M9.4 3.4H3.4v17.2h6" />',
  'external-link':
    '<path d="M14 3.4h6.6V10" /> <path d="M10.4 13.6 20.6 3.4" />' +
    ' <path d="M17.4 13v7.6H3.4V6.6H11" />',
  'maximize-2':
    '<path d="M14.4 3.4h6.2v6.2" /> <path d="m20.6 3.4-6.6 6.6" />' +
    ' <path d="M9.6 20.6H3.4v-6.2" /> <path d="m3.4 20.6 6.6-6.6" />',
  'minimize-2':
    '<path d="M14.4 3.6v6h6" /> <path d="m20.6 3.4-6.2 6.2" />' +
    ' <path d="M9.6 20.4v-6h-6" /> <path d="m3.4 20.6 6.2-6.2" />',
  link:
    '<path d="M9.6 6.6H4.4v10.8h5.2" /> <path d="M14.4 6.6h5.2v10.8h-5.2" />' +
    ' <path d="M8 12h8" />',
  copy: '<path d="M8.4 8.4h12.2v12.2H8.4z" /> <path d="M15.6 3.4H3.4v12.2h5" />',
  pin:
    '<path d="M9 2.8h6v2.4h-1.4v6l2.8 2.8v2H7.6v-2l2.8-2.8v-6H9z" /> <path d="M12 16v5.2" />',
  ellipsis:
    '<path d="M4.4 10.8h2.4v2.4H4.4z" /> <path d="M10.8 10.8h2.4v2.4h-2.4z" />' +
    ' <path d="M17.2 10.8h2.4v2.4h-2.4z" />',

  // ── release-scope SECTION TYPES (#810 PR 4): one per renderer family,
  // cut in this set's own construction so the corpus TOC's section rows are
  // own-set under every style (icons:check refuses a fallback).
  'rs-prose':
    '<path d="M5.4 3.4h13.2v17.2H5.4z" /> <path d="M8.4 8h7.2" opacity=".75" />' +
    ' <path d="M8.4 11.6h7.2" opacity=".75" /> <path d="M8.4 15.2h4.6" opacity=".75" />',
  'rs-paired-lists':
    '<path d="M3.4 4.4h7.2v15.2H3.4z" /> <path d="M13.4 4.4h7.2v15.2h-7.2z" />' +
    ' <path d="M5.4 9h3.2" opacity=".6" /> <path d="M5.4 12.6h3.2" opacity=".6" />' +
    ' <path d="M15.4 9h3.2" opacity=".6" /> <path d="M15.4 12.6h3.2" opacity=".6" />',
  'rs-table':
    '<path d="M3.4 4.4h17.2v15.2H3.4z" /> <path d="M3.4 9.4h17.2" />' +
    ' <path d="M12 9.4v10.2" opacity=".6" /> <path d="M3.4 14.6h17.2" opacity=".45" />',
  'rs-table-expansion':
    '<path d="M3.4 3.4h17.2v9H3.4z" /> <path d="M3.4 8h17.2" opacity=".6" />' +
    ' <path d="M5.8 15.2h12.4v5.4H5.8z" opacity=".5" /> <path d="m10.4 10 1.6 1.6 1.6-1.6" />',
  'rs-rules':
    '<path d="M4.4 5.6h2.4v2.4H4.4z" opacity=".6" /> <path d="M4.4 10.8h2.4v2.4H4.4z" opacity=".6" />' +
    ' <path d="M4.4 16h2.4v2.4H4.4z" opacity=".6" /> <path d="M10.4 6.8h10.2" />' +
    ' <path d="M10.4 12h10.2" /> <path d="M10.4 17.2h6.6" />',
  'rs-state-machine':
    '<path d="M2.4 8.4h7.2v7.2H2.4z" /> <path d="M14.4 8.4h7.2v7.2h-7.2z" />' +
    ' <path d="M9.6 12h4.8" opacity=".6" /> <path d="m12.8 10.4 1.6 1.6-1.6 1.6" />',
  'rs-spec-cards':
    '<path d="M5.4 8.4h15.2v12.2H5.4z" /> <path d="M3.4 15.4V3.4h11.8" opacity=".5" />' +
    ' <path d="M8.6 12.6h8.6" opacity=".6" /> <path d="M8.6 16.2h5.4" opacity=".6" />',
  'rs-wireframe':
    '<path d="M3.4 4.4h17.2v15.2H3.4z" /> <path d="M3.4 8.6h17.2" />' +
    ' <path d="M6.4 11.6h6v4.8h-6z" opacity=".5" /> <path d="M15.2 12.4h2.8" opacity=".5" />' +
    ' <path d="M15.2 15.4h2.8" opacity=".5" />',
};

/** Every id this set re-cuts. Sorted so the gate's output is stable. */
export const STARSHIP_ICON_IDS: string[] = Object.keys(STARSHIP_SYMBOLS).sort();
